import { derived, get, writable } from 'svelte/store';
import type Web3 from 'web3';
import { loadWeb3 } from '../utils/loadWeb3/loadWeb3';

/**
 * Replaces the abandoned `svelte-web3` package (last published 2023, declares
 * `svelte: ^3`) with the small slice of it this app actually used.
 *
 * The old library never imported web3 itself — it read `globalThis.Web3`, which
 * was supplied by a `<script src=".../web3@latest/...">` tag in the navbar. That
 * meant the wallet layer silently ran whatever major jsdelivr served (4.x by the
 * time this was written) while the bundle pinned web3 1.10. These stores import
 * web3 directly instead, so one version is used everywhere and it is the one in
 * package.json.
 *
 * web3 itself is loaded by `setProvider` (see `loadWeb3`): the stores hold no
 * instance until a wallet connects, so pages that only render them don't pay
 * for the library.
 */

type EvmState = {
	web3: Web3 | null;
	selectedAccount: string | null;
	chainId: string | null;
	connected: boolean;
	accounts: string[];
};

const initialState: EvmState = {
	web3: null,
	selectedAccount: null,
	chainId: null,
	connected: false,
	accounts: [],
};

const state = writable<EvmState>({ ...initialState });

/** web3 4.x reports chain ids as bigint; the old store exposed hex strings. */
const toHexChainId = (value: bigint | number | string) => {
	if (typeof value === 'string') {
		return value.startsWith('0x') ? value : `0x${BigInt(value).toString(16)}`;
	}

	return `0x${BigInt(value).toString(16)}`;
};

const getEthereum = () => {
	if (typeof window === 'undefined') {
		return undefined;
	}

	return window.ethereum;
};

const readProviderState = async (instance: Web3, accounts: string[]) => {
	const chainId = toHexChainId(await instance.eth.getChainId());

	state.set({
		web3: instance,
		selectedAccount: accounts[0] ?? null,
		chainId,
		connected: true,
		accounts,
	});
};

/**
 * Kept as module-level handlers (rather than inline closures) so a later
 * `setProvider` can detach the previous provider's listeners — otherwise every
 * reconnect stacks another handler on the same injected provider.
 */
const handleAccountsChanged = async (accounts: string[]) => {
	const instance = get(state).web3;

	if (!instance) {
		return;
	}

	await readProviderState(instance, accounts);
};

const handleChainChanged = async (chainId: string) => {
	state.update((current) => ({
		...current,
		chainId: toHexChainId(chainId),
	}));
};

const handleDisconnect = () => {
	state.set({ ...initialState });
};

const detachListeners = (provider: {
	removeListener?: (event: string, handler: (...args: never[]) => void) => void;
}) => {
	provider.removeListener?.('accountsChanged', handleAccountsChanged);
	provider.removeListener?.('chainChanged', handleChainChanged);
	provider.removeListener?.('disconnect', handleDisconnect);
};

export const defaultEvmStores = {
	/**
	 * Prompts the injected wallet for accounts and populates the stores. Throws
	 * when no wallet is present so callers can fall back to a manual address —
	 * `Web3NavBar` relies on this rejecting rather than resolving empty.
	 */
	setProvider: async () => {
		const ethereum = getEthereum();

		if (!ethereum) {
			throw new Error(
				'Please authorize browser extension (MetaMask or similar)'
			);
		}

		if (get(state).web3) {
			detachListeners(ethereum);
		}

		const accounts: string[] = await ethereum.request({
			method: 'eth_requestAccounts',
		});

		const Web3Library = await loadWeb3();
		const instance = new Web3Library(ethereum);

		ethereum.on?.('accountsChanged', handleAccountsChanged);
		ethereum.on?.('chainChanged', handleChainChanged);
		ethereum.on?.('disconnect', handleDisconnect);

		await readProviderState(instance, accounts);
	},

	disconnect: async () => {
		const ethereum = getEthereum();

		if (ethereum) {
			detachListeners(ethereum);
		}

		state.set({ ...initialState });
	},
};

export const web3 = derived(state, ($state) => $state.web3);
export const selectedAccount = derived(
	state,
	($state) => $state.selectedAccount
);
export const chainId = derived(state, ($state) => $state.chainId);
export const connected = derived(state, ($state) => $state.connected);
